import { Control, Controller, FieldErrors } from 'react-hook-form';
import { StanInput, StanTimezoneSelector } from '@/components';
import { ProductSelector } from '../ProductSelector';
import { StanToggle } from '@/components/core-components/StanToggle/StanToggle';
import { DateTimePicker } from '../DateTimePicker/DateTimePicker';
import type { EditFlowFormData } from '../../validations/editFlow';

interface EditFlowDetailsSectionProps {
    control: Control<EditFlowFormData>;
    errors: FieldErrors<EditFlowFormData>;
    isDateTriggerActive: boolean;
    isCartAbandonedTriggerActive: boolean;
    showRemoveRecipients: boolean;
    selectedDate?: Date;
    selectedTime: number;
    onDateChange: (date: Date | undefined) => void;
    onTimeChange: (time: number) => void;
    updatesDisabled: boolean;
}

export const EditFlowDetailsSection = ({
    control,
    errors,
    isDateTriggerActive,
    isCartAbandonedTriggerActive,
    showRemoveRecipients,
    selectedDate,
    selectedTime,
    onDateChange,
    onTimeChange,
    updatesDisabled,
}: EditFlowDetailsSectionProps) => {
    return (
        <div className='flex flex-col gap-2'>
            <Controller
                name='flowName'
                control={control}
                render={({ field }) => (
                    <div>
                        <label className='text-label-xs-bold'>Flow Title</label>
                        <StanInput
                            {...field}
                            placeholder='Name Your Flow'
                            error={!!errors.flowName}
                            hintText={errors.flowName?.message}
                            disabled={updatesDisabled}
                        />
                    </div>
                )}
            />

            {!isDateTriggerActive && (
                <div className='flex flex-col gap-2'>
                    <Controller
                        name='selectedProducts'
                        control={control}
                        render={({ field }) => (
                            <ProductSelector
                                label='Products'
                                selectedProductIds={field.value || []}
                                onProductsChange={field.onChange}
                                onlyPaidProducts={isCartAbandonedTriggerActive}
                                error={!!errors.selectedProducts}
                                hintText={errors.selectedProducts?.message}
                            />
                        )}
                    />

                    {!isCartAbandonedTriggerActive && (
                        <>
                            <div className='flex items-center gap-2'>
                                <Controller
                                    name='showRemoveRecipients'
                                    control={control}
                                    render={({ field }) => (
                                        <StanToggle
                                            checked={field.value || false}
                                            onChange={field.onChange}
                                            toggleSize='sm'
                                        />
                                    )}
                                />
                                <label className='text-dark text-xs font-medium'>
                                    Remove customer from flow after product purchase
                                </label>
                            </div>

                            {showRemoveRecipients && (
                                <Controller
                                    name='selectedExcludeProducts'
                                    control={control}
                                    render={({ field }) => (
                                        <ProductSelector
                                            label='Exclude Products'
                                            selectedProductIds={field.value || []}
                                            onProductsChange={field.onChange}
                                            includeAllProductsTag={false}
                                            includeNewReferralTag={false}
                                        />
                                    )}
                                />
                            )}
                        </>
                    )}
                </div>
            )}

            {isDateTriggerActive && (
                <div className='flex flex-col gap-2'>
                    <Controller
                        name='selectedTimezone'
                        control={control}
                        render={({ field }) => (
                            <div>
                                <label htmlFor='timezone-selector' className='text-label-xs-bold'>
                                    Timezone
                                </label>
                                <StanTimezoneSelector
                                    selectedTimezone={field.value || ''}
                                    onTimezoneChange={field.onChange}
                                    disabled={updatesDisabled}
                                />
                            </div>
                        )}
                    />

                    <div>
                        <DateTimePicker
                            selectedDate={selectedDate}
                            selectedTime={selectedTime}
                            onDateChange={onDateChange}
                            onTimeChange={onTimeChange}
                            minDate={new Date()}
                            disabled={updatesDisabled}
                            error={!!errors.selectedTime || !!errors.selectedDate}
                            errorMessage={errors.selectedTime?.message || errors.selectedDate?.message}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
